import { useState, useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { 
  Heart, 
  MapPin, 
  Calendar, 
  Scale,
  Ruler,
  User,
  Award,
  Search,
  Filter,
  Trophy,
  Share2,
  Copy,
  Check,
  X,
  ImageOff,
  RefreshCw
} from "lucide-react";

// Lazy loading image component with skeleton and error handling
const LazyImage = ({ src, alt, className, onLoad: externalOnLoad }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const imgRef = useRef(null);
  const containerRef = useRef(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '100px', threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleLoad = useCallback(() => {
    setIsLoaded(true);
    setHasError(false);
    if (externalOnLoad) externalOnLoad();
  }, [externalOnLoad]);

  const handleError = useCallback(() => {
    if (retryCount < 2) {
      // Retry with cache-busting
      setRetryCount(prev => prev + 1);
    } else {
      setHasError(true);
      setIsLoaded(true);
    }
  }, [retryCount]);

  const handleRetry = () => {
    setHasError(false);
    setIsLoaded(false);
    setRetryCount(0);
  };

  // Build URL with retry cache-busting
  const imageUrl = retryCount > 0 ? `${src}?retry=${retryCount}` : src;

  return (
    <div ref={containerRef} className="relative bg-gray-100 min-h-[200px] overflow-hidden">
      {/* Loading skeleton */}
      {!isLoaded && (
        <div className="absolute inset-0 bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 animate-pulse">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-[var(--deep-ocean)]/20 border-t-[var(--deep-ocean)] rounded-full animate-spin mx-auto mb-2" />
              <span className="text-xs text-[var(--river-stone)]">Loading photo...</span>
            </div>
          </div>
        </div>
      )}

      {/* Error state */}
      {hasError && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
          <div className="text-center p-4">
            <ImageOff className="w-10 h-10 text-[var(--river-stone)] mx-auto mb-2" />
            <p className="text-sm text-[var(--river-stone)] mb-2">Photo unavailable</p>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRetry}
              className="text-xs"
            >
              <RefreshCw className="w-3 h-3 mr-1" /> Retry
            </Button>
          </div>
        </div>
      )}

      {/* Actual image - only load when in view */}
      {isInView && !hasError && (
        <img
          ref={imgRef}
          src={imageUrl}
          alt={alt}
          className={`${className} transition-opacity duration-500 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={handleLoad}
          onError={handleError}
        />
      )}
    </div>
  );
};

const FISHING_TYPES = [
  "Fresh Water", "Specimen", "Bass", "Fly Fishing", 
  "Rock and Surf", "Boat", "Offshore", "Estuary"
];

const CommunityPage = () => {
  const { token, user } = useAuth();
  const [catches, setCatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [fishingTypeFilter, setFishingTypeFilter] = useState("all");
  const [sortBy, setSortBy] = useState("recent");
  const [shareModalOpen, setShareModalOpen] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchCommunityFeed();
  }, [searchQuery, fishingTypeFilter, sortBy]);

  const fetchCommunityFeed = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "50", sort_by: sortBy });
      if (searchQuery) params.append("search", searchQuery);
      if (fishingTypeFilter && fishingTypeFilter !== "all") params.append("fishing_type", fishingTypeFilter);
      
      const response = await axios.get(`${API}/catches/feed?${params.toString()}`);
      setCatches(response.data);
    } catch (error) {
      toast.error("Failed to load community feed");
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async (catchId) => {
    if (!token) {
      toast.error("Please login to like catches");
      return;
    }
    try {
      const response = await axios.post(`${API}/catches/${catchId}/like`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCatches(catches.map(c => 
        c.id === catchId ? { ...c, likes: response.data.likes } : c
      ));
    } catch (error) {
      toast.error("Failed to like catch");
    }
  };

  const handleVote = async (catchId) => {
    if (!token) {
      toast.error("Please login to vote for Member of Month");
      return;
    }
    try {
      const response = await axios.post(`${API}/catches/${catchId}/vote`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCatches(catches.map(c => 
        c.id === catchId ? { ...c, votes: response.data.votes } : c
      ));
      
      if (response.data.voted) {
        toast.success("Vote recorded for Member of Month!");
      } else {
        toast.info("Vote removed");
      }
    } catch (error) {
      toast.error("Failed to vote");
    }
  };

  const handleShare = (catchItem, platform) => {
    const shareUrl = `${window.location.origin}/community?catch=${catchItem.id}`;
    const shareText = `Check out this ${catchItem.species} catch by ${catchItem.user_name} on FishingFanatix!`;
    
    switch (platform) {
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`, '_blank');
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`, '_blank');
        break;
      case 'copy':
        navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        toast.success("Link copied to clipboard!");
        setTimeout(() => setCopied(false), 2000);
        break;
    }
    setShareModalOpen(null);
  };

  const getFishingTypeBadgeClass = (type) => {
    const classes = {
      "Fresh Water": "bg-blue-100 text-blue-800",
      "Specimen": "bg-purple-100 text-purple-800",
      "Bass": "bg-green-100 text-green-800",
      "Fly Fishing": "bg-amber-100 text-amber-800",
      "Rock and Surf": "bg-gray-100 text-gray-800",
      "Boat": "bg-cyan-100 text-cyan-800",
      "Offshore": "bg-indigo-100 text-indigo-800",
      "Estuary": "bg-teal-100 text-teal-800"
    };
    return classes[type] || "bg-gray-100 text-gray-800";
  };

  return (
    <Layout title="Community" subtitle="See what fellow anglers are catching">
      {/* Search and Filter Bar */}
      <div className="mb-8 space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--river-stone)]" />
            <Input
              type="text"
              placeholder="Search by species, member, or location..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 input-field"
              data-testid="search-input"
            />
          </div>
          <div className="flex gap-2">
            <Select value={fishingTypeFilter} onValueChange={setFishingTypeFilter}>
              <SelectTrigger className="w-[180px]" data-testid="fishing-type-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Fishing Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {FISHING_TYPES.map(type => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[140px]" data-testid="sort-by-filter">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="recent">Most Recent</SelectItem>
                <SelectItem value="popular">Most Liked</SelectItem>
                <SelectItem value="votes">Top Voted</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {/* Active filters display */}
        {(searchQuery || fishingTypeFilter !== "all") && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-[var(--river-stone)]">Active filters:</span>
            {searchQuery && (
              <Badge variant="secondary" className="flex items-center gap-1">
                "{searchQuery}"
                <X className="w-3 h-3 cursor-pointer" onClick={() => setSearchQuery("")} />
              </Badge>
            )}
            {fishingTypeFilter !== "all" && (
              <Badge variant="secondary" className="flex items-center gap-1">
                {fishingTypeFilter}
                <X className="w-3 h-3 cursor-pointer" onClick={() => setFishingTypeFilter("all")} />
              </Badge>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="break-inside-avoid">
              <Card className="bg-white card-shadow border-0 overflow-hidden animate-pulse">
                <div className="bg-gray-200 h-48" />
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-gray-200" />
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded w-24 mb-2" />
                      <div className="h-3 bg-gray-200 rounded w-16" />
                    </div>
                  </div>
                  <div className="h-5 bg-gray-200 rounded w-32 mb-2" />
                  <div className="h-4 bg-gray-200 rounded w-20" />
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      ) : catches.length > 0 ? (
        <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6">
          {catches.map((catchItem, index) => (
            <motion.div
              key={catchItem.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.03 }}
              className="break-inside-avoid"
            >
              <Card className={`bg-white card-shadow border-0 overflow-hidden catch-card ${
                catchItem.is_member_of_month_catch ? "ring-4 ring-yellow-400" : ""
              }`}>
                {catchItem.photo_url && (
                  <div className="relative">
                    <LazyImage
                      src={`${process.env.REACT_APP_BACKEND_URL}${catchItem.photo_url}`}
                      alt={catchItem.species}
                      className="w-full object-cover"
                    />
                    {catchItem.is_member_of_month_catch && (
                      <div className="absolute top-3 left-3 flex items-center gap-2 z-10">
                        <Badge className="bg-yellow-400 text-yellow-900">
                          <Award className="w-3 h-3 mr-1" /> Catch of the Month
                        </Badge>
                      </div>
                    )}
                  </div>
                )}
                <CardContent className="p-5">
                  {/* User Info */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-[var(--deep-ocean)]/10 flex items-center justify-center">
                      <User className="w-5 h-5 text-[var(--deep-ocean)]" />
                    </div>
                    <div>
                      <p className="font-semibold text-[var(--deep-ocean)]">{catchItem.user_name}</p>
                      <p className="text-xs text-[var(--river-stone)]">
                        {new Date(catchItem.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  {/* Catch Info */}
                  <div className="mb-3">
                    <h3 className="text-lg font-bold text-[var(--deep-ocean)]">{catchItem.species}</h3>
                    <Badge className={getFishingTypeBadgeClass(catchItem.fishing_type)}>
                      {catchItem.fishing_type}
                    </Badge>
                  </div>

                  <div className="space-y-2 text-sm text-[var(--river-stone)]">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      <span>{catchItem.location}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>{new Date(catchItem.date_caught).toLocaleDateString()}</span>
                    </div>
                    {(catchItem.weight || catchItem.length) && (
                      <div className="flex items-center gap-4">
                        {catchItem.weight && (
                          <div className="flex items-center gap-1">
                            <Scale className="w-4 h-4" />
                            <span>{catchItem.weight} kg</span>
                          </div>
                        )}
                        {catchItem.length && (
                          <div className="flex items-center gap-1">
                            <Ruler className="w-4 h-4" />
                            <span>{catchItem.length} cm</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {catchItem.notes && (
                    <p className="mt-3 text-sm text-[var(--river-stone)] italic border-l-2 border-[var(--deep-ocean)]/20 pl-3">
                      "{catchItem.notes}"
                    </p>
                  )}

                  {/* Action Buttons */}
                  <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {/* Like Button */}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-[var(--river-stone)] hover:text-red-500"
                        onClick={() => handleLike(catchItem.id)}
                        data-testid={`like-catch-${catchItem.id}`}
                      >
                        <Heart className="w-4 h-4 mr-1" />
                        {catchItem.likes || 0}
                      </Button>
                      
                      {/* Vote Button */}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-[var(--river-stone)] hover:text-yellow-500"
                        onClick={() => handleVote(catchItem.id)}
                        title="Vote for Member of Month"
                        data-testid={`vote-catch-${catchItem.id}`}
                      >
                        <Trophy className="w-4 h-4 mr-1" />
                        {catchItem.votes || 0}
                      </Button>
                    </div>
                    
                    {/* Share Button */}
                    <div className="relative">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-[var(--river-stone)] hover:text-[var(--deep-ocean)]"
                        onClick={() => setShareModalOpen(shareModalOpen === catchItem.id ? null : catchItem.id)}
                        data-testid={`share-catch-${catchItem.id}`}
                      >
                        <Share2 className="w-4 h-4" />
                      </Button>
                      
                      {/* Share Dropdown */}
                      {shareModalOpen === catchItem.id && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="absolute right-0 bottom-full mb-2 bg-white rounded-lg card-shadow-deep p-2 z-50 min-w-[150px]"
                        >
                          <button
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 rounded-md transition"
                            onClick={() => handleShare(catchItem, 'whatsapp')}
                            data-testid={`share-whatsapp-${catchItem.id}`}
                          >
                            <svg className="w-4 h-4 text-green-500" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                            </svg>
                            WhatsApp
                          </button>
                          <button
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 rounded-md transition"
                            onClick={() => handleShare(catchItem, 'facebook')}
                            data-testid={`share-facebook-${catchItem.id}`}
                          >
                            <svg className="w-4 h-4 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                            </svg>
                            Facebook
                          </button>
                          <button
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 rounded-md transition"
                            onClick={() => handleShare(catchItem, 'copy')}
                            data-testid={`share-copy-${catchItem.id}`}
                          >
                            {copied ? (
                              <Check className="w-4 h-4 text-green-500" />
                            ) : (
                              <Copy className="w-4 h-4 text-gray-500" />
                            )}
                            Copy Link
                          </button>
                        </motion.div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      ) : (
        <Card className="bg-white card-shadow border-0">
          <CardContent className="p-12 text-center">
            <User className="w-16 h-16 text-[var(--river-stone)] mx-auto mb-4" />
            <h3 className="text-xl font-bold text-[var(--deep-ocean)] mb-2">
              {searchQuery || fishingTypeFilter !== "all" ? "No catches found" : "No catches yet"}
            </h3>
            <p className="text-[var(--river-stone)]">
              {searchQuery || fishingTypeFilter !== "all" 
                ? "Try adjusting your search or filters"
                : "Be the first to share your catch with the community!"}
            </p>
          </CardContent>
        </Card>
      )}
    </Layout>
  );
};

export default CommunityPage;
